import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore, useUIStore } from '@/lib/core/stores';
import type { AppContext } from '@/lib/core/types/modules';
import type {
  GetCatalogProductsV1Params,
  ProductListItemDto,
  ProductSortKey,
  ProductTagFacetDto,
} from '@/services/platform/api/model';
import { getCatalogProducts } from '@/services/platform/data/shop/catalog';
import { abortAfter } from '@/services/custom-fetch';
import { useShopFavoriteIndex } from '@/services/platform/data/shop';
import { useInfiniteScroll } from '@/lib/core/hooks';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { Icon } from '@/components/ui/icon';
import { ShopHeaderActions } from '@/components/shared/shop/layout/ShopHeaderActions';
import { ShopProductListEmpty } from '@/components/shared/shop/product/list/empty';
import { ShopProductListView2Grid } from '@/components/shared/shop/product/list/views/view-2-grid';
import {
  ShopProductListFilterSheet,
  type ShopProductListFilterSheetFilters,
} from '@/components/shared/shop/product/list/filters/sheet';

const PAGE_SIZE = 12;
const REQUEST_TIMEOUT_MS = 15000;

/** A structured search suggestion resolved to a `getCatalogProducts` filter. Free-text
 * popular/recent terms have no such mapping and don't produce a `ShopSearchResultFilter`. */
export type ShopSearchResultFilter =
  | { type: 'category'; slug: string; label: string }
  | { type: 'subcategory'; slug: string; label: string }
  | { type: 'subSubcategory'; slug: string; label: string }
  | { type: 'collection'; nodeKey: string; label: string };

export interface ShopSearchResultsProps {
  filter: ShopSearchResultFilter;
  sectionSlug?: string;
  sectionName?: string;
  context?: AppContext;
  onBack: () => void;
}

export function ShopSearchResults({
  filter,
  sectionSlug,
  sectionName,
  context,
  onBack,
}: ShopSearchResultsProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [products, setProducts] = useState<ProductListItemDto[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [brands, setBrands] = useState<string[] | null>(null);
  const [colors, setColors] = useState<ProductTagFacetDto[] | null>(null);
  const [minPrice, setMinPrice] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ProductSortKey | undefined>(undefined);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedColorSlugs, setSelectedColorSlugs] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number] | undefined>(undefined);
  const requestIdRef = useRef(0);
  const {
    shopFavoriteLists,
    favoriteListGuidByProductId,
    refetch: refetchFavoriteIndex,
  } = useShopFavoriteIndex();

  const filterKey = filter.type === 'collection' ? filter.nodeKey : filter.slug;

  const handleOpenFilter = () => setIsFilterOpen(true);
  const handleCloseFilter = () => setIsFilterOpen(false);

  const handleApplyFilters = (filters: ShopProductListFilterSheetFilters) => {
    setSortBy(filters.sortBy);
    setSelectedBrands(filters.brands);
    setSelectedColorSlugs(filters.colorSlugs);
    setPriceRange(filters.priceRange);
  };

  const handleRemoveFilters = () => {
    setSortBy(undefined);
    setSelectedBrands([]);
    setSelectedColorSlugs([]);
    setPriceRange(undefined);
  };

  const hasActiveFilter = Boolean(sortBy) || selectedBrands.length > 0 || selectedColorSlugs.length > 0;
  const isPriceRangeChanged = Boolean(
    priceRange && (priceRange[0] !== (minPrice ?? 0) || priceRange[1] !== (maxPrice ?? 0))
  );
  const activeFilterCount =
    (sortBy ? 1 : 0) + selectedBrands.length + selectedColorSlugs.length + (isPriceRangeChanged ? 1 : 0);

  const applyFacets = (result: Awaited<ReturnType<typeof getCatalogProducts>>) => {
    // Only the fully unfiltered fetch should (re)populate the filter sheet's facets —
    // a filter-narrowed fetch's facets must not reshuffle what the sheet offers.
    if (hasActiveFilter) return;
    setBrands(result?.brands ?? null);
    setColors(result?.colors ?? null);
    setMinPrice(result?.minPrice ?? null);
    setMaxPrice(result?.maxPrice ?? null);
  };

  useEffect(() => {
    handleRemoveFilters();
  }, [filterKey]);

  const buildParams = useCallback(
    (pageNum: number): GetCatalogProductsV1Params => ({
      pageSize: PAGE_SIZE,
      page: pageNum,
      section: sectionSlug ? [sectionSlug] : undefined,
      category: filter.type === 'category' ? [filter.slug] : undefined,
      subcategory: filter.type === 'subcategory' ? [filter.slug] : undefined,
      subsubcategory: filter.type === 'subSubcategory' ? [filter.slug] : undefined,
      collection: filter.type === 'collection' ? [filter.nodeKey] : undefined,
      brand: selectedBrands.length ? selectedBrands : undefined,
      // No dedicated `color` param exists on the catalog search API yet — color slugs
      // ride on the generic `slugs` filter, same as `ShopProductListApi`.
      slugs: selectedColorSlugs.length ? selectedColorSlugs : undefined,
      priceMin: priceRange?.[0],
      priceMax: priceRange?.[1],
      sortBy,
    }),
    [filter, sectionSlug, selectedBrands, selectedColorSlugs, priceRange, sortBy]
  );

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const timeoutId = abortAfter(controller, REQUEST_TIMEOUT_MS);
    setPage(1);
    setIsLoading(true);
    setHasNextPage(false);
    getCatalogProducts(buildParams(1), locale, controller.signal)
      .then(result => {
        if (requestIdRef.current !== requestId) return;
        const items = result?.products?.items ?? [];
        setProducts(items);
        setTotalItems(result?.products?.totalItems ?? 0);
        setHasNextPage(items.length > 0 && (result?.products?.hasNextPage ?? false));
        applyFacets(result);
      })
      .catch(error => {
        if (requestIdRef.current !== requestId) return;
        console.error('ShopSearchResults: failed to fetch catalog products', error);
        setProducts([]);
        setTotalItems(0);
        setHasNextPage(false);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (requestIdRef.current === requestId) setIsLoading(false);
      });
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [buildParams, locale]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasNextPage) return;
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const timeoutId = abortAfter(controller, REQUEST_TIMEOUT_MS);
    const nextPage = page + 1;
    setLoadingMore(true);
    getCatalogProducts(buildParams(nextPage), locale, controller.signal)
      .then(result => {
        if (requestIdRef.current !== requestId) return;
        const items = result?.products?.items ?? [];
        setProducts(prev => [...prev, ...items]);
        setHasNextPage(items.length > 0 && (result?.products?.hasNextPage ?? false));
        applyFacets(result);
        setPage(nextPage);
      })
      .catch(error => {
        if (requestIdRef.current !== requestId) return;
        console.error('ShopSearchResults: failed to fetch more catalog products', error);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (requestIdRef.current === requestId) setLoadingMore(false);
      });
  }, [loadingMore, hasNextPage, page, buildParams, locale]);

  const sentinelRef = useInfiniteScroll(handleLoadMore, hasNextPage && !isLoading && !loadingMore);

  const handleProductClick = (publicGuid: string | undefined) => {
    if (!publicGuid) return;
    navigate(`${pathname}/product_${publicGuid}`);
  };

  useEffect(() => {
    return () => setScreenHeadOverride(null);
  }, [setScreenHeadOverride]);

  useEffect(() => {
    setScreenHeadOverride({
      title: filter.label,
      titleSuffix: sectionName,
      onBack,
      ariaLabel: t('App.Labels.GoBack'),
      rightActions: <ShopHeaderActions context={context} hideSearch />,
    });
  }, [t, filter.label, sectionName, onBack, context, setScreenHeadOverride]);

  return (
    <div className="flex flex-col gap-0">
      <div className="border-jncquoi-neutral-200 mx-4 flex items-center justify-between border-b py-0">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] tracking-[0.5px] uppercase">
          {t('App.Shop.Search.ResultsFound', { count: totalItems })}
        </p>
        <button
          type="button"
          onClick={handleOpenFilter}
          aria-label={t('App.Shop.Filter.Title')}
          className="flex min-h-11 items-center justify-center gap-1 py-2 pr-2"
        >
          {activeFilterCount > 0 && (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px] uppercase">
              ({activeFilterCount})
            </span>
          )}
          <Icon name="filter-list" width={20} height={20} className="text-jncquoi-neutral-1000" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <SpinLoading wrapperClassName="min-h-0" spinnerClassName="h-6 w-6" />
        </div>
      ) : products.length === 0 ? (
        <ShopProductListEmpty />
      ) : (
        <ShopProductListView2Grid
          products={products}
          locale={locale}
          onProductClick={handleProductClick}
          favoriteLists={shopFavoriteLists}
          favoriteListGuidByProductId={favoriteListGuidByProductId}
          refetchFavoriteIndex={refetchFavoriteIndex}
        />
      )}

      <ShopProductListFilterSheet
        open={isFilterOpen}
        onOpenChange={openNext => {
          if (!openNext) handleCloseFilter();
        }}
        sortBy={sortBy}
        brands={brands ?? []}
        colors={colors ?? []}
        minPrice={minPrice ?? 0}
        maxPrice={maxPrice ?? 0}
        selectedBrands={selectedBrands}
        selectedColorSlugs={selectedColorSlugs}
        priceRange={priceRange ?? [minPrice ?? 0, maxPrice ?? 0]}
        onApply={handleApplyFilters}
        resultCount={products.length}
        isLoadingResults={isLoading}
      />

      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          {loadingMore && <SpinLoading wrapperClassName="min-h-0" spinnerClassName="h-6 w-6" />}
        </div>
      )}
    </div>
  );
}
